import React from 'react';
import { components } from 'react-select';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { Theme, ThemeProps } from '@theme/base';
import { mq } from '@styles/breakpoints';
import { HelpArticleResult } from '@model/search';
import { TestId } from '@components/test-ids';
import { useI18NextContext } from '@components/hooks';
import { ComponentContainer } from '@styles/layout';
import { SearchContainerDropdown } from '@components/search/tabs/common/search-container/dropdown/SearchContainerDropdown';
import { AlgoliaIndex, AlgoliaSearch } from '@components/common/algolia-search';
import { Typeahead } from '@components/common/typeahead/Typeahead';
import { NoOptionsMessage } from '@components/common/typeahead/components/no-options-message/NoOptionsMessage';
import { Icon } from '@components/common/generic-icon';
import { Icons } from '@model/common/icons';

export interface HelpCentreSearchProps {
  onInputChange: (payload: string) => void;
  onChange: (payload: HelpArticleResult) => void;
  value: string;
  selectedValue: HelpArticleResult | null;
  options: Array<HelpArticleResult>;
  active: boolean;
  onFocus: () => void;
  onBlur: () => void;
}

const SearchWrapper = styled.div(({ theme }: ThemeProps) => ({
  background: theme.custom.colors.group1.base,
  padding: `${theme.custom.spacing.xxLarge}px 0px`,
  textAlign: 'center'
}));

const SearchContainer = styled.div(({ theme }: ThemeProps) => ({
  margin: 'auto',
  marginBottom: theme.custom.spacing.xxLarge,
  [mq.medium]: {
    maxWidth: '50%'
  }
}));

const SearchTitle = styled.h2(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h2 as any),
  color: theme.custom.colors.white
}));

const SearchBox = styled.div({
  textAlign: 'left'
});

const SearchSubTitle = styled.p(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  color: theme.custom.colors.white,
  display: 'flex',
  flexDirection: 'column',
  [mq.medium]: {
    display: 'block',
    span: {
      padding: `0 ${theme.custom.spacing.xSmall}px`
    }
  }
}));

const OptionWrapper = styled.div(({ theme }: ThemeProps) => ({
  cursor: 'pointer',
  marginBottom: theme.custom.spacing.small,
  ['&:first-of-type']: {
    marginTop: theme.custom.spacing.small
  },
  ['> i']: {
    color: theme.custom.colors.group4.base
  },
  ['&:hover']: {
    color: theme.custom.colors.group1.base,
    ['> i']: {
      color: theme.custom.colors.group1.base
    }
  }
}));

export const HelpCentreSearch = ({
  value,
  selectedValue,
  onInputChange,
  onChange,
  options,
  active,
  onFocus,
  onBlur
}: HelpCentreSearchProps) => {
  const t = useI18NextContext();
  const theme: Theme = useTheme();
  const renderNoOptions = () => (
    <NoOptionsMessage message={t('help-centre__no-articles')} retryMessage={t('help-centre__no-articles-try-again')} />
  );
  const renderOption = (props: any) => {
    const { data } = props;
    return (
      <components.Option {...(props as any)}>
        <OptionWrapper>
          <Icon name={Icons.CHEVRON_RIGHT} css={{ marginRight: theme.custom.spacing.small }} />
          {data.title}
        </OptionWrapper>
      </components.Option>
    );
  };

  return (
    <SearchWrapper data-testid={TestId.helpCentre.search.main}>
      <ComponentContainer>
        <SearchContainer>
          <SearchTitle>{t('help-centre__heading')}</SearchTitle>
          <SearchBox>
            <SearchContainerDropdown
              title={'help-centre__placeholder'}
              icon={'mhi-search'}
              hideChevron
              render={() => (
                <AlgoliaSearch index={AlgoliaIndex.HELP_CENTRE} facetFilters={null}>
                  <Typeahead
                    id="help-centre"
                    placeholder="help-centre__placeholder"
                    onInputChange={onInputChange}
                    onChange={onChange}
                    inputValue={value}
                    value={selectedValue}
                    options={options}
                    active={active}
                    filterOption={() => true}
                    getOptionLabel={(article) => article.title}
                    getValueLabel={(article) => article.title}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    renderNoOptionsMessage={renderNoOptions}
                    renderOption={renderOption}
                  />
                </AlgoliaSearch>
              )}
            />
          </SearchBox>
          <SearchSubTitle>
            <span>{t('help-centre__subheading1')}</span>
            <span>{t('help-centre__subheading2')}</span>
          </SearchSubTitle>
        </SearchContainer>
      </ComponentContainer>
    </SearchWrapper>
  );
};
